/**
 * Store submission settings (P29 phase 1, api/openapi.yaml Store Apps): the organization's Google Play service account keys,
 * an app's store apps and its per-environment track rules. Keys and store apps are added and changed by platform operators
 * only (the API answers 403 to everyone else); the rules by admins and operators. The key travels once, as a multipart file.
 * Phase 2: a build's submissions (`/submissions`) and Submit.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { DistributionRuleWrite, StoreAppCreate, StoreAppPatch, SubmissionCreateRequest } from '@hangar/shared';
import { api } from './client';
import { SigningCredential, multipart } from './endpointsSigning';

const enc = encodeURIComponent;

export const StoreApp = z.object({
  id: z.string(),
  appId: z.string(),
  storeKind: z.string(),
  platform: z.enum(['ios', 'android']),
  identifier: z.string(),
  externalAppId: z.string().nullable().optional(),
  storeAccount: z.object({ id: z.string(), kind: z.string(), name: z.string(), credentialId: z.string().nullable().optional() }),
  isPrivateManaged: z.boolean(),
  managedPublishing: z.boolean(),
  expectedUploadCert: z.object({ sha1: z.string().optional(), sha256: z.string().optional() }).nullable().optional(),
  createdAt: z.string(),
});
export type StoreApp = z.infer<typeof StoreApp>;
const StoreAppList = z.object({ data: z.array(StoreApp), nextCursor: z.string().nullable() });

export const DistributionRule = z.object({
  id: z.string(),
  storeAppId: z.string(),
  environmentId: z.string(),
  environment: z.string().nullable(),
  allowedTracks: z.array(z.string()),
  maxInitialRollout: z.number().nullable(),
});
export type DistributionRule = z.infer<typeof DistributionRule>;
export const DistributionRules = z.object({ appId: z.string(), rules: z.array(DistributionRule), etag: z.string() });
export type DistributionRules = z.infer<typeof DistributionRules>;
const KeyList = z.object({ data: z.array(SigningCredential), nextCursor: z.string().nullable() });

export const storeAppsKey = (appId: string) => ['apps', appId, 'store-apps'] as const;
export const rulesKey = (appId: string) => ['apps', appId, 'distribution-rules'] as const;
export const playKeysKey = ['credentials', 'play_service_account'] as const;

export const listStoreApps = (appId: string) => api('GET', '/store-apps', { schema: StoreAppList, query: { appId, limit: 200 } }).then((p) => p.data);
export const createStoreApp = (body: StoreAppCreate) => api('POST', '/store-apps', { schema: StoreApp, body });
export const updateStoreApp = (id: string, body: StoreAppPatch) => api('PATCH', `/store-apps/${enc(id)}`, { schema: StoreApp, body });
export const getDistributionRules = (appId: string) => api('GET', `/apps/${enc(appId)}/distribution-rules`, { schema: DistributionRules });
/** `etag` is the one the rules were read with: a change made meanwhile answers 412 instead of being overwritten. */
export const putDistributionRules = (appId: string, rules: DistributionRuleWrite[], etag: string) =>
  api('PUT', `/apps/${enc(appId)}/distribution-rules`, { schema: DistributionRules, body: { rules }, ifMatch: etag });

/** The organization's active Google Play service account keys (metadata only). */
export const listPlayKeys = () =>
  api('GET', '/credentials', { schema: KeyList, query: { kind: 'play_service_account', limit: 200 } }).then((p) => p.data.filter((c) => c.status !== 'revoked'));

/** Uploads a Google Play service account key (the JSON file Google Cloud downloads); owners, admins and operators. */
export function uploadPlayKey(name: string, file: File, idempotencyKey: string) {
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify({ kind: 'play_service_account', name, managedBy: 'hangar' })], { type: 'application/json' }));
  form.append('file', file, file.name);
  return multipart('/credentials', form, idempotencyKey);
}

export const useStoreApps = (appId: string | undefined) => useQuery({ queryKey: storeAppsKey(appId ?? ''), queryFn: () => listStoreApps(appId!), enabled: Boolean(appId) });
export const useDistributionRules = (appId: string | undefined) => useQuery({ queryKey: rulesKey(appId ?? ''), queryFn: () => getDistributionRules(appId!), enabled: Boolean(appId) });
export const usePlayKeys = (enabled: boolean) => useQuery({ queryKey: playKeysKey, queryFn: listPlayKeys, enabled });

/** Refetches what a store change touches: the app's store apps and rules, its profiles (which name store apps) and the keys. */
export function useRefreshStores(appId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: storeAppsKey(appId) });
    void qc.invalidateQueries({ queryKey: rulesKey(appId) });
    void qc.invalidateQueries({ queryKey: playKeysKey });
  };
}

export const SubmissionEvent = z.object({ id: z.string(), fromStatus: z.string().nullable(), toStatus: z.string().nullable(), source: z.string(), createdAt: z.string() });
export const Submission = z.object({
  id: z.string(),
  buildId: z.string(),
  storeApp: z.object({ id: z.string(), identifier: z.string(), platform: z.string() }).nullable(),
  track: z.string(),
  status: z.string(),
  rolloutStatus: z.string().nullable(),
  rolloutFraction: z.number().nullable(),
  actorLabel: z.string().nullable(),
  error: z.object({ category: z.string(), code: z.string(), message: z.string() }).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  events: z.array(SubmissionEvent).default([]),
});
export type Submission = z.infer<typeof Submission>;
const SubmissionList = z.object({ data: z.array(Submission), nextCursor: z.string().nullable() });

/** Statuses the store has not answered yet: the list refreshes while one is shown. */
export const ACTIVE_SUBMISSION_STATUSES: ReadonlySet<string> = new Set(['queued', 'submitting', 'processing', 'in_review']);

export const submissionsKey = (buildId: string) => ['builds', buildId, 'submissions'] as const;
export const listBuildSubmissions = (buildId: string) => api('GET', '/submissions', { schema: SubmissionList, query: { buildId, include: 'events', limit: 50 } }).then((p) => p.data);
export const createSubmission = (body: SubmissionCreateRequest, idempotencyKey: string) => api('POST', '/submissions', { schema: Submission, body, idempotencyKey });

/** A build's submissions, newest first; polled every 5 s while one is on its way. */
export const useBuildSubmissions = (buildId: string, enabled: boolean) =>
  useQuery({
    queryKey: submissionsKey(buildId), queryFn: () => listBuildSubmissions(buildId), enabled,
    refetchInterval: (q) => ((q.state.data ?? []).some((s) => ACTIVE_SUBMISSION_STATUSES.has(s.status)) ? 5_000 : false),
  });
